import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaCheck } from "react-icons/fa";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import PageHeading from "../PageHeading";
import AddAmbulanceForm from "./AddAmbulance";
import ExportOptions from "../ExportOptions";
import ConfirmActionModal from "../ConfirmDeleteModal";
import TableSkeleton from "../TableSkeleton";

const API_URL = "https://api.muktihospital.com/api/ambulance";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const AmbulanceModal = ({ isOpen, onClose, onSuccess, editing }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 print-hidden">
      <button
        className="absolute top-2 right-2 text-xl text-gray-600 hover:text-gray-800"
        onClick={onClose}
      >
        ✕
      </button>
      <AddAmbulanceForm
        initialData={editing}
        onSuccess={onSuccess}
        onCancel={onClose}
      />
    </div>
  );
};

const AmbulanceManager = () => {
  const [ambulances, setAmbulances] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const itemsPerPage = 5;

  const fetchAmbulances = async () => {
    setLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
      });
      const list = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
        ? res.data.data
        : [];

      // Check if createdAt exists; otherwise, fall back to id
      const hasCreatedAt = list.length > 0 && list[0].createdAt;
      let sortedList;
      if (hasCreatedAt) {
        // console.log("Sorting by createdAt descending");
        sortedList = [...list].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
        );
      } else {
        // console.log("Sorting by id descending (fallback)");
        sortedList = [...list].sort((a, b) => {
          // Handle string or numeric IDs
          const idA = typeof a.id === "string" ? parseInt(a.id, 10) || a.id : a.id;
          const idB = typeof b.id === "string" ? parseInt(b.id, 10) || b.id : b.id;
          return idB > idA ? 1 : idB < idA ? -1 : 0;
        });
      }
      setAmbulances(sortedList);
      setFiltered(sortedList);
      setCurrentPage(1);
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Failed to load ambulances.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAmbulances();
  }, []);

  useEffect(() => {
    const term = search.toLowerCase();
    const result = ambulances.filter((item) =>
      item.vehicleNumber?.toLowerCase().includes(term)
    );
    setFiltered(result);
    setCurrentPage(1); // Reset to first page on search
  }, [search, ambulances]);

  const filteredList = filtered.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );
  const totalPages = Math.ceil(filtered.length / itemsPerPage);

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredList.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (ambulanceId) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(ambulanceId)
        ? prevSelectedRows.filter((id) => id !== ambulanceId)
        : [...prevSelectedRows, ambulanceId]
    );
  };

  const handleDelete = async (ambulanceIds) => {
    try {
      if (Array.isArray(ambulanceIds)) {
        await Promise.all(
          ambulanceIds.map((id) =>
            axios.delete(`${API_URL}/${id}`, {
              headers: { "x-api-key": API_KEY },
            })
          )
        );
        toast.success(
          `${ambulanceIds.length} ambulance(s) deleted successfully.`
        );
      } else {
        await axios.delete(`${API_URL}/${ambulanceIds}`, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Ambulance deleted successfully.");
      }
      setSelectedRows([]);
      fetchAmbulances();
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete ambulance(s).");
    }
  };

  const openModal = (data = null) => {
    setEditing(data);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
  };

  const handleSuccess = (ambulanceData) => {
    // console.log("handleSuccess - Received ambulanceData:", ambulanceData);
    toast.success(
      editing
        ? "Ambulance updated successfully."
        : "Ambulance added successfully."
    );
    if (ambulanceData && ambulanceData.id) {
      if (editing) {
        // For edits, update the ambulance and move it to the top
        setAmbulances((prev) => {
          const updatedList = prev.filter((item) => item.id !== ambulanceData.id);
          const newList = [ambulanceData, ...updatedList];
          // console.log("Updated ambulances:", newList); // Debug state
          return newList;
        });
        setFiltered((prev) => {
          const updatedList = prev.filter((item) => item.id !== ambulanceData.id);
          const newList = [ambulanceData, ...updatedList];
          // console.log("Updated filtered:", newList); // Debug state
          return newList;
        });
      } else {
        // For additions, prepend the new ambulance
        setAmbulances((prev) => {
          const newList = [ambulanceData, ...prev];
          // console.log("Added ambulances:", newList); // Debug state
          return newList;
        });
        setFiltered((prev) => {
          const newList = [ambulanceData, ...prev];
          // console.log("Added filtered:", newList); // Debug state
          return newList;
        });
      }
      setCurrentPage(1); // Reset to first page to show the ambulance
    } else {
      console.error("handleSuccess - Invalid ambulanceData:", ambulanceData);
      toast.error("Failed to process ambulance data. Refreshing list...");
      fetchAmbulances(); // Fallback to fetching only if data is invalid
    }
    closeModal();
  };

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  const headers = [
    "SL No",
    "Vehicle Number",
    "Model",
    "Year Made",
    "Driver Name",
    "Driver License",
    "Driver Contact",
    "Note",
    "Vehicle Type",
  ];
  const dataMapper = (item, index) =>
    [
      filtered.indexOf(item) + 1,
      item.vehicleNumber || "",
      item.vehicleModel || "",
      item.yearMade || "",
      item.driverName || "",
      item.driverLicense || "",
      item.driverContact || "",
      item.note || "",
      item.vehicleType || "",
    ].join("\t");

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Ambulance" },
    { label: "Ambulance List" },
  ];

  return (
    <div>
      <ToastContainer position="top-right" className="print-hidden" />
      <div className="print-hidden">
        <PageHeading title="Ambulance List" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Ambulance List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3">
            <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
              placeholder="Search by vehicle number"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <div className="flex items-center justify-between w-full gap-3">
              <button
                onClick={() => openModal()}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0"
              >
                + Add Ambulance
              </button>
              <ExportOptions
                data={filtered}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Ambulance Reports"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>
        <div>
          <div className="overflow-x-auto print-wrapper">
            <table className="min-w-[1440px] w-full divide-y divide-gray-200 print-table">
              <thead>
                <tr className="bg-gray-100">
                  <th className="text-left print-hidden-in-print">
                    <div className="relative left-3 -top-[2px]">
                      <input
                        type="checkbox"
                        id="agreement"
                        className="hidden peer print-hidden"
                        onChange={handleSelectAll}
                        checked={
                          selectedRows.length === filteredList.length &&
                          filteredList.length > 0
                        }
                      />
                      <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                        <FaCheck className="text-slate-100" />
                      </span>
                      <label
                        htmlFor="agreement"
                        className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                      ></label>
                    </div>
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    No.
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Vehicle Number
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Model
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Year Made
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Driver Name
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Driver License
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Driver Contact
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Note
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                    Vehicle Type
                  </th>
                  <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <TableSkeleton
                    rowCount={itemsPerPage}
                    columns={[
                      { width: 40, height: 16, isCircle: false, className: "pl-3 print-hidden-in-print" }, // Checkbox
                      { width: 60, height: 16, className: "pl-3" }, // No.
                      { width: 120, height: 16, className: "pl-3" }, // Vehicle Number
                      { width: 120, height: 16, className: "pl-3" }, // Model
                      { width: 100, height: 16, className: "pl-3" }, // Year Made
                      { width: 150, height: 16, className: "pl-3" }, // Driver Name
                      { width: 120, height: 16, className: "pl-3" }, // Driver License
                      { width: 120, height: 16, className: "pl-3" }, // Driver Contact
                      { width: 150, height: 16, className: "pl-3" }, // Note
                      { width: 100, height: 16, className: "pl-3" }, // Vehicle Type
                      { width: 100, height: 16, className: "pl-3 print-hidden-in-print" }, // Actions
                    ]}
                  />
                ) : (
                  filteredList.map((item, i) => (
                    <tr key={item.id} className="border-b hover:bg-gray-50">
                      <td className="text-left capitalize font-inter font-medium text-sm text-M-heading-color print-hidden-in-print">
                        <div className="relative left-3 -top-[3px]">
                          <input
                            type="checkbox"
                            id={`ambulance-${i}`}
                            className="hidden peer print-hidden"
                            checked={selectedRows.includes(item.id)}
                            onChange={() => handleRowSelect(item.id)}
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor={`ambulance-${i}`}
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {(currentPage - 1) * itemsPerPage + i + 1}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.vehicleNumber || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.vehicleModel || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.yearMade || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.driverName || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.driverLicense || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.driverContact || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        {item.note || "N/A"}
                      </td>
                      <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                        <span
                          className={`inline-block px-3 py-1 text-xs rounded-full font-medium ${
                            item.vehicleType === "Own"
                              ? "bg-green-100 text-green-700"
                              : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {item.vehicleType || "N/A"}
                        </span>
                      </td>
                      <td className="p-3 text-left space-x-2 print-hidden-in-print flex shrink-0">
                        <button
                          onClick={() => openModal(item)}
                          className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                          title="Edit"
                        >
                          <FiEdit />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedRows([item.id]);
                            setDeleteModalOpen(true);
                          }}
                          className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                          title="Delete"
                        >
                          <RiDeleteBin6Line />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="p-5 text-end">
              <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                <li>
                  <button
                    className="table-pagination-item disabled:opacity-50"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    <MdKeyboardArrowLeft />
                  </button>
                </li>
                {totalPages <= 10
                  ? [...Array(totalPages)].map((_, i) => (
                      <li key={i}>
                        <button
                          className={`table-pagination-item ${
                            currentPage === i + 1
                              ? "bg-M-primary-color text-white"
                              : ""
                          }`}
                          onClick={() => handlePageChange(i + 1)}
                        >
                          {i + 1}
                        </button>
                      </li>
                    ))
                  : (() => {
                      const pages = [];
                      const firstPages = 5;
                      const lastPages = 3;

                      for (
                        let i = 1;
                        i <= Math.min(firstPages, totalPages);
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      if (totalPages > firstPages + lastPages) {
                        pages.push(
                          <li key="ellipsis" className="table-pagination-item">
                            ...
                          </li>
                        );
                      }

                      for (
                        let i = Math.max(
                          firstPages + 1,
                          totalPages - lastPages + 1
                        );
                        i <= totalPages;
                        i++
                      ) {
                        pages.push(
                          <li key={i}>
                            <button
                              className={`table-pagination-item ${
                                currentPage === i
                                  ? "bg-M-primary-color text-white"
                                  : ""
                              }`}
                              onClick={() => handlePageChange(i)}
                            >
                              {i}
                            </button>
                          </li>
                        );
                      }

                      return pages;
                    })()}
                <li>
                  <button
                    className="table-pagination-item"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    <MdKeyboardArrowRight />
                  </button>
                </li>
              </ul>
            </div>
          )}
        </div>
      </div>
      <AmbulanceModal
        isOpen={modalOpen}
        onClose={closeModal}
        onSuccess={handleSuccess}
        editing={editing}
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="ambulance"
        title="Confirm Delete"
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default AmbulanceManager;